import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Popover, Stack, Typography } from '@mui/material';
import SpaRoundedIcon    from '@mui/icons-material/SpaRounded';
import AddRoundedIcon    from '@mui/icons-material/AddRounded';
import CheckRoundedIcon  from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useGrowSetup } from '../../GrowSetupContext';
import { AuthContext } from '../../components/signin/AuthContext';

// ── Palette: one accent color per setup index ────────────────────────────────
const SETUP_COLORS = ['var(--v4-cyan)', 'var(--v4-green)', 'var(--v4-purple)', 'var(--v4-orange)', 'var(--v4-red)', 'var(--v4-blue)'];
const getColor = (i) => SETUP_COLORS[i % SETUP_COLORS.length];

// ── CSS animations ────────────────────────────────────────────────────────────
const ANIM_CSS = `
@keyframes gspSlideDown { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:translateY(0)} }
@keyframes gspFadeIn    { from{opacity:0;transform:translateY(6px)}   to{opacity:1;transform:translateY(0)} }
@keyframes gspPulse     { 0%,100%{box-shadow:0 0 0 0 color-mix(in srgb, var(--v4-green) 53%, transparent)} 50%{box-shadow:0 0 0 6px color-mix(in srgb, var(--v4-green) 0%, transparent)} }
@keyframes gspSpin      { to{transform:rotate(360deg)} }
@keyframes gspCheck     { 0%{transform:scale(0)} 60%{transform:scale(1.2)} 100%{transform:scale(1)} }
`;

// ── Single setup card inside the picker ─────────────────────────────────────

function SetupCard({ setup, isActive, color, index, onSelect, switching }) {
  const [hov, setHov] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => !isActive && !switching && onSelect(setup)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 1.75, py: 1.25,
        borderRadius: '14px',
        cursor: isActive ? 'default' : switching ? 'wait' : 'pointer',
        background: isActive
          ? `color-mix(in srgb, ${color} 8%, transparent)`
          : hov ? 'rgba(var(--v4-fg-rgb),0.05)' : 'transparent',
        border: `1px solid ${isActive ? `color-mix(in srgb, ${color} 20%, transparent)` : 'rgba(var(--v4-fg-rgb),0.04)'}`,
        transition: 'all 0.2s cubic-bezier(0.22,1,0.36,1)',
        animation: `gspFadeIn 0.3s ${index * 50}ms both`,
        transform: hov && !isActive ? 'translateX(3px)' : 'none',
        boxShadow: isActive ? `0 0 20px color-mix(in srgb, ${color} 9%, transparent)` : 'none',
      }}
    >
      {/* Avatar */}
      <Box sx={{
        width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
        background: `color-mix(in srgb, ${color} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 27%, transparent)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative',
        transition: 'all 0.2s',
        boxShadow: isActive ? `0 0 14px color-mix(in srgb, ${color} 27%, transparent)` : 'none',
      }}>
        <SpaRoundedIcon sx={{ fontSize: 18, color }} />
        {/* Active pulse dot */}
        {isActive && (
          <Box sx={{
            position: 'absolute', bottom: -2, right: -2,
            width: 9, height: 9, borderRadius: '50%',
            background: 'var(--v4-green)',
            border: '1.5px solid rgba(var(--v4-surface2-rgb),1)',
            boxShadow: '0 0 6px var(--v4-green)',
            animation: 'gspPulse 2s infinite',
          }} />
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontWeight: isActive ? 800 : 600,
          color: isActive ? 'var(--v4-text)' : 'rgba(var(--v4-muted-rgb),0.65)',
          fontSize: '0.82rem',
          lineHeight: 1.2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          transition: 'color 0.2s',
        }}>
          {setup.name}
        </Typography>
        <Typography sx={{
          fontSize: '0.62rem',
          color: isActive ? 'var(--v4-green)' : 'rgba(var(--v4-muted-rgb),0.28)',
          fontWeight: 700, mt: 0.2,
          transition: 'color 0.2s',
        }}>
          {isActive ? '● Aktiv' : `ID: ${setup.id}`}
        </Typography>
      </Box>

      {/* Right indicator */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {isActive && (
          <CheckRoundedIcon sx={{
            fontSize: 16, color,
            animation: 'gspCheck 0.3s cubic-bezier(0.22,1,0.36,1) both',
          }} />
        )}
        {!isActive && switching && (
          <CircularProgress size={14} sx={{ color: 'rgba(var(--v4-cyan-rgb),0.4)' }} />
        )}
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function GrowSetupPicker() {
  const { grow_setup, setGrowSetup } = useGrowSetup();
  const { token }                    = useContext(AuthContext);
  const navigate                     = useNavigate();

  const [setups,    setSetups]    = useState([]);
  const [open,      setOpen]      = useState(false);
  const [switching, setSwitching] = useState(false);
  const anchorRef = useRef(null);

  // Load setups once (same logic as NavBar)
  useEffect(() => {
    axios.post('/get_all_grow_setups', {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (Array.isArray(res.data) && res.data.length > 0) {
        setSetups(res.data);
        const saved = parseInt(localStorage.getItem('grow_setup'), 10);
        if (res.data.some(s => s.id === saved)) setGrowSetup(saved);
        else { setGrowSetup(res.data[0].id); localStorage.setItem('grow_setup', res.data[0].id); }
      }
    }).catch(console.error);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentSetup = setups.find(s => s.id === grow_setup) || null;
  const currentIndex = setups.findIndex(s => s.id === grow_setup);
  const currentColor = currentIndex >= 0 ? getColor(currentIndex) : 'var(--v4-cyan)';

  const handleSelect = useCallback(async (setup) => {
    setSwitching(true);
    // short visual pause so the spinner is noticeable
    await new Promise(r => setTimeout(r, 340));
    localStorage.setItem('grow_setup', setup.id);
    setGrowSetup(setup.id);
    setSwitching(false);
    setOpen(false);
  }, [setGrowSetup]);

  return (
    <>
      <style>{ANIM_CSS}</style>

      {/* ── Trigger button ── */}
      <Box
        ref={anchorRef}
        onClick={() => setOpen(v => !v)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.25, py: 0.65,
          borderRadius: '12px',
          cursor: 'pointer',
          background: open ? 'rgba(var(--v4-cyan-rgb),0.1)' : 'rgba(var(--v4-cyan-rgb),0.06)',
          border: `1px solid ${open ? 'rgba(var(--v4-cyan-rgb),0.35)' : 'rgba(var(--v4-cyan-rgb),0.15)'}`,
          transition: 'all 0.18s',
          maxWidth: { xs: 140, sm: 190 },
          minWidth: 90,
          '&:hover': {
            background: 'rgba(var(--v4-cyan-rgb),0.12)',
            border: '1px solid rgba(var(--v4-cyan-rgb),0.3)',
          },
        }}
      >
        {/* Icon */}
        <Box sx={{
          width: 22, height: 22, borderRadius: '7px', flexShrink: 0,
          background: `color-mix(in srgb, ${currentColor} 13%, transparent)`, border: `1px solid color-mix(in srgb, ${currentColor} 27%, transparent)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <SpaRoundedIcon sx={{ fontSize: 13, color: currentColor }} />
        </Box>

        {/* Name */}
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 700,
          color: 'rgba(var(--v4-text-rgb),0.85)',
          flex: 1, minWidth: 0,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {currentSetup?.name ?? '—'}
        </Typography>

        {/* Arrow */}
        <KeyboardArrowDownRoundedIcon sx={{
          fontSize: 16, color: 'rgba(var(--v4-cyan-rgb),0.5)', flexShrink: 0,
          transition: 'transform 0.22s cubic-bezier(0.22,1,0.36,1)',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        }} />
      </Box>

      {/* ── Dropdown panel ── */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        PaperProps={{
          sx: {
            mt: 0.75,
            background: 'rgba(var(--v4-surface2-rgb),0.97)',
            border: '1px solid rgba(var(--v4-cyan-rgb),0.18)',
            borderRadius: '18px',
            backdropFilter: 'blur(32px)',
            boxShadow: '0 20px 60px rgba(var(--v4-shadow-rgb),calc(0.6*var(--v4-shadow-k))), 0 0 40px rgba(var(--v4-cyan-rgb),0.06)',
            minWidth: 240, maxWidth: 300,
            overflow: 'hidden',
            animation: 'gspSlideDown 0.22s cubic-bezier(0.22,1,0.36,1) both',
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2, pt: 1.75, pb: 1,
          borderBottom: '1px solid rgba(var(--v4-cyan-rgb),0.07)',
        }}>
          <Typography sx={{
            fontSize: '0.58rem', fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.14em',
            color: 'rgba(var(--v4-cyan-rgb),0.45)',
          }}>
            Grow Setups
          </Typography>
        </Box>

        {/* Setup list */}
        <Box sx={{ px: 1, py: 1 }}>
          {setups.length === 0 && (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={20} sx={{ color: 'rgba(var(--v4-cyan-rgb),0.4)' }} />
            </Box>
          )}
          {setups.map((setup, i) => (
            <SetupCard
              key={setup.id}
              setup={setup}
              isActive={setup.id === grow_setup}
              color={getColor(i)}
              index={i}
              onSelect={handleSelect}
              switching={switching && setup.id !== grow_setup}
            />
          ))}
        </Box>

        {/* Divider + New setup */}
        <Box sx={{ px: 1, pb: 1, borderTop: '1px solid rgba(var(--v4-cyan-rgb),0.07)', pt: 1 }}>
          <Box
            onClick={() => { setOpen(false); navigate('/new_grow_setup'); }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 1.75, py: 1.1, borderRadius: '12px', cursor: 'pointer',
              color: 'rgba(var(--v4-cyan-rgb),0.55)',
              transition: 'all 0.18s',
              '&:hover': {
                background: 'rgba(var(--v4-cyan-rgb),0.08)',
                color: 'var(--v4-cyan)',
              },
            }}
          >
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              background: 'rgba(var(--v4-cyan-rgb),0.08)', border: '1px dashed rgba(var(--v4-cyan-rgb),0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AddRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'inherit' }}>
                Neues Setup
              </Typography>
              <Typography sx={{ fontSize: '0.62rem', color: 'rgba(var(--v4-cyan-rgb),0.35)', mt: 0.1 }}>
                Grow Setup erstellen
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
